import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const HCAPTCHA_SITEKEY = process.env.REACT_APP_HCAPTCHA_SITEKEY;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Register() {
    const { register, user } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [captchaToken, setCaptchaToken] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const captchaRef = useRef(null);

    useEffect(() => {
        if (user) navigate(user.has_birth_info ? "/dashboard" : "/onboarding", { replace: true });
    }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!termsAccepted) {
            toast.error("Please accept the Terms of Service and Privacy Policy");
            return;
        }
        if (!captchaToken) {
            toast.error("Please complete the captcha");
            return;
        }
        setLoading(true);
        try {
            await register(email, password, name, captchaToken, termsAccepted);
            toast.success("Your journey begins.");
            navigate("/onboarding");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Registration failed");
            captchaRef.current?.resetCaptcha();
            setCaptchaToken(null);
        } finally {
            setLoading(false);
        }
    };

    const googleSignup = () => {
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <>
            <Navbar />
            <main className="min-h-screen flex items-center justify-center px-6 pt-24 pb-12">
                <div className="glass p-10 max-w-md w-full fade-in">
                    <p className="label-tracking text-gold text-center">A new constellation awakens</p>
                    <h1 className="font-serif text-4xl text-center mt-3">Begin your journey</h1>

                    <form onSubmit={onSubmit} className="mt-8 space-y-5">
                        <div>
                            <label className="label-tracking">Name</label>
                            <input
                                type="text" required value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none transition-colors"
                                data-testid="register-name"
                            />
                        </div>
                        <div>
                            <label className="label-tracking">Email</label>
                            <input
                                type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none transition-colors"
                                data-testid="register-email"
                            />
                        </div>
                        <div>
                            <label className="label-tracking">Password</label>
                            <input
                                type="password" required minLength={6} value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-gold outline-none transition-colors"
                                data-testid="register-password"
                            />
                        </div>
                        {HCAPTCHA_SITEKEY && (
                            <div className="flex justify-center" data-testid="register-captcha">
                                <HCaptcha
                                    ref={captchaRef}
                                    sitekey={HCAPTCHA_SITEKEY}
                                    onVerify={setCaptchaToken}
                                    onExpire={() => setCaptchaToken(null)}
                                    theme="dark"
                                />
                            </div>
                        )}
                        <label className="flex items-start gap-3 text-xs text-muted cursor-pointer">
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                className="mt-0.5 accent-[#E6C387]"
                                data-testid="register-terms-checkbox"
                            />
                            <span>
                                I am at least 18 years old and I agree to the{" "}
                                <Link to="/terms" target="_blank" className="text-gold hover:underline" data-testid="register-terms-link">
                                    Terms of Service
                                </Link>{" "}
                                and{" "}
                                <Link to="/privacy" target="_blank" className="text-gold hover:underline" data-testid="register-privacy-link">
                                    Privacy Policy
                                </Link>
                                .
                            </span>
                        </label>
                        <button type="submit" disabled={loading || !termsAccepted || (HCAPTCHA_SITEKEY && !captchaToken)} className="btn-gold-solid w-full disabled:opacity-50" data-testid="register-submit">
                            {loading ? "Casting..." : "Create Account"}
                        </button>
                    </form>

                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
                        <span className="relative bg-[#0A0A1A] px-4 text-xs text-muted uppercase tracking-widest mx-auto block w-fit">or</span>
                    </div>

                    <button onClick={googleSignup} className="w-full btn-gold" data-testid="google-register">
                        Continue with Google
                    </button>

                    <p className="text-center text-sm text-muted mt-8">
                        Already aligned?{" "}
                        <Link to="/login" className="text-gold hover:underline" data-testid="link-login">
                            Sign in
                        </Link>
                    </p>
                </div>
            </main>
        </>
    );
}
